import { RotateCcw, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ColumnPaginationDots } from "./ColumnPaginationDots";

/**
 * MobileFooter - Fixed bottom navigation bar for mobile devices
 * 
 * Layout (bottom to top):
 * - Safe area padding (iPhone home indicator)
 * - Pagination dots (centered, subtle)
 * - Action buttons: "Find my thread" (left) + Quick Add FAB (right)
 */
export const MobileFooter = ({
  onFindMyThread,
  onQuickAddOpen,
  isQuickAddOpen,
  threadDriftGlow,
  totalColumns = 0,
  boardRef,
}) => {
  return (
    <div 
      className="fixed bottom-8 left-0 right-0 z-50 md:hidden"
      data-testid="mobile-footer-bar"
    >
      {/* Main footer content - Level 2 elevation */}
      <div 
        className="backdrop-blur-md border-t border-white/40"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.96) 100%)',
          boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.06), 0 -1px 8px rgba(0, 0, 0, 0.03)',
        }}
      >
        {/* Action buttons row - increased top padding for breathing room */}
        <div className="px-5 pt-4 pb-4 flex items-center justify-between gap-4">
          <div className="flex flex-col items-start">
            <motion.button
              onClick={onFindMyThread}
              data-testid="reset-button"
              className={`flex items-center gap-1.5 bg-[#0CBCBF] text-white rounded-2xl px-5 py-3 text-[15px] font-semibold hover:bg-[#0A9A9D] transition-all active:scale-95 ${threadDriftGlow ? 'animate-thread-drift' : ''}`}
              style={{
                boxShadow: 'var(--elevation-2-accent)',
                transform: 'scale(1.10)',
              }}
              animate={{
                scale: [1.10, 1.12, 1.10],
              }}
              transition={{
                duration: 0.8,
                repeat: Infinity,
                repeatDelay: 6,
                ease: "easeInOut",
              }}
            >
              <RotateCcw size={17} strokeWidth={2.5} />
              <span>Where was i?</span>
            </motion.button>
            <span className="text-[12px] text-stone-400 mt-2 ml-1">Take a breath and pick up your thread.</span>
          </div>

          {/* Spacer to push FAB to right */}
          <div className="flex-1" />

          {!isQuickAddOpen && (
            <button
              onClick={onQuickAddOpen}
              className="w-11 h-11 bg-[#F58227] hover:bg-[#D96D19] text-white rounded-full flex items-center justify-center transition-colors active:scale-95"
              style={{
                boxShadow: 'var(--elevation-2-accent)',
              }}
              data-testid="quick-add-fab-mobile"
              aria-label="Quick add task"
            >
              <Plus size={22} strokeWidth={2.5} />
            </button>
          )}
        </div>

        {/* Pagination dots - centered, subtle */}
        {totalColumns > 1 && (
          <div className="pb-1">
            <ColumnPaginationDots 
              totalColumns={totalColumns}
              boardRef={boardRef}
            />
          </div>
        )}
      </div>
      
      {/* Safe area spacer for iPhone home indicator */}
      <div 
        className="safe-area-bottom"
        style={{ background: 'rgba(255,255,255,0.96)' }}
      />
    </div>
  );
};

/**
 * DesktopFooterButtons - Fixed buttons for desktop (Find my thread + FAB)
 * Level 2 elevation - floating above content
 */
export const DesktopFooterButtons = ({
  onFindMyThread,
  onQuickAddOpen,
  isQuickAddOpen,
  threadDriftGlow,
}) => {
  return (
    <>
      {/* Desktop-only: Fixed "Where was i?" button - centered, same level as FAB */}
      <div className="hidden md:flex md:flex-col md:items-center fixed bottom-16 left-1/2 -translate-x-1/2 z-50">
        <motion.button
          onClick={onFindMyThread}
          data-testid="reset-button-desktop"
          className={`flex items-center gap-1.5 bg-[#0CBCBF] text-white rounded-2xl px-5 py-3 text-[15px] font-semibold hover:bg-[#0A9A9D] transition-all active:scale-95 ${threadDriftGlow ? 'animate-thread-drift' : ''}`}
          style={{
            boxShadow: 'var(--elevation-2-accent)',
            transform: 'scale(1.10)',
          }}
          animate={{
            scale: [1.10, 1.115, 1.10],
          }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            repeatDelay: 7,
            ease: "easeInOut",
          }}
        >
          <RotateCcw size={17} strokeWidth={2.5} />
          <span>Where was i?</span>
        </motion.button>
        <span className="text-[12px] text-stone-400 mt-2">Take a breath and pick up your thread.</span>
      </div>

      {/* Desktop-only: Floating Quick Add Button */}
      <AnimatePresence>
        {!isQuickAddOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={onQuickAddOpen}
            className="hidden md:flex fixed bottom-16 right-6 w-[52px] h-[52px] bg-[#F58227] hover:bg-[#D96D19] text-white rounded-full items-center justify-center z-[60] transition-colors"
            style={{
              boxShadow: 'var(--elevation-2-accent)',
            }}
            data-testid="quick-add-fab-desktop"
            aria-label="Quick add task"
          >
            <Plus size={22} />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileFooter;
